import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type DayRibbonSegmentKind = 'focus' | 'meeting' | 'personal';

export interface DayRibbonSegment {
  kind: DayRibbonSegmentKind;
  /** Minutes since midnight, local time. */
  startMinutes: number;
  /** Minutes since midnight, local time. */
  endMinutes: number;
  /** Tooltip / label text — meeting title, focus-block label, etc. */
  title: string;
  /**
   * Optional source id — typically the DailyFocusBlock.id for focus
   * segments. Used by `activeBlockId` to highlight the bar the
   * executive is currently inside with a copper ring.
   */
  id?: string;
}

interface DayRibbonProps {
  segments: DayRibbonSegment[];
  /** Working-day start hour. Defaults to 07:00. */
  startHour?: number;
  /** Working-day end hour. Defaults to 19:00. */
  endHour?: number;
  className?: string;
  /** Optional list of hour-marker labels along the bottom axis. */
  hourMarkers?: number[];
  /**
   * When set, the focus segment whose `id` matches gets a copper
   * (#C97A2B) ring + glow so the active block reads at-a-glance on
   * the day timeline. Pass the currently-active DailyFocusBlock.id.
   */
  activeBlockId?: string;
  /**
   * When true, play the spec's entrance choreography on mount: focus
   * bars sweep open via `scaleX(0)→1` left-to-right at a 150ms
   * cadence, focus time-labels fade in together at 1.7s, and meeting /
   * personal ticks fade from 0 to their final opacity over 500ms.
   * Defaults to false (static render) so the ribbon doesn't animate
   * surprisingly on the workspace surfaces that just want it as
   * day-shape context.
   */
  animate?: boolean;
}

function formatHM(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// DayRibbon — a horizontal one-day timeline. Focus blocks read as
// glowing primary-coloured bars with a small time-label, meetings as
// neutral grey bars, personal events as dashed lavender outlines, and
// a vertical now-marker tracks the current time. Hover any segment to
// see its title + start/end via a tooltip.
//
// The component is layout-only — it doesn't fetch anything; callers
// flatten their calendar / focus-block / personal-event data into
// `DayRibbonSegment[]` and pass it in.
export function DayRibbon({
  segments,
  startHour = 7,
  endHour = 19,
  hourMarkers = [7, 10, 13, 16, 19],
  className,
  animate = false,
  activeBlockId,
}: DayRibbonProps) {
  const span = (endHour - startHour) * 60;
  const x = (m: number) => {
    const pct = ((m - startHour * 60) / span) * 100;
    return Math.max(0, Math.min(100, pct));
  };

  // Live "now" marker — refresh once a minute so it advances visibly.
  const [nowMinutes, setNowMinutes] = useState<number>(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setNowMinutes(d.getHours() * 60 + d.getMinutes());
    };
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Entrance choreography state. Defaults to "revealed" when animate
  // is off so workspace surfaces render statically. When animate is
  // true, hold the initial state for one frame then flip — focus bars
  // sweep open from scaleX(0), labels fade in at 1.7s, ticks fade in
  // over 500ms.
  const [revealed, setRevealed] = useState(!animate);
  useEffect(() => {
    if (!animate) return;
    const id = window.setTimeout(() => setRevealed(true), 20);
    return () => window.clearTimeout(id);
  }, [animate]);

  return (
    <div className={cn('w-full', className)}>
      <div className="relative h-[58px]">
        <div className="pointer-events-none absolute inset-x-0 top-[31px] h-px bg-border" />
        {segments.map((seg, i) => {
          const left = x(seg.startMinutes);
          const width = x(seg.endMinutes) - left;
          if (width <= 0) return null;
          const tooltip = `${seg.title} · ${formatHM(seg.startMinutes)} to ${formatHM(seg.endMinutes)}`;
          if (seg.kind === 'focus') {
            // Focus bars sweep from scaleX(0) to scaleX(1) on a 150ms
            // cadence indexed across ALL ribbon items, kicking off at
            // t=760ms relative to the ribbon's own mount (which itself
            // is slot 4 of the Brief's .rise ladder).
            //
            // When this bar IS the activeBlock, wrap it in a copper
            // (#C97A2B) border + glow per the cross-surface colour
            // role split — copper is the brand/active accent against
            // the focus blue, so the executive sees at a glance which
            // bar they're currently inside.
            const isActive = !!activeBlockId && seg.id === activeBlockId;
            return (
              <div
                key={i}
                title={tooltip}
                className={cn(
                  'group absolute top-[20px] h-[22px] rounded-[11px] bg-gradient-to-b from-[#A9CBFF] to-primary shadow-[0_0_20px_rgba(26,108,240,0.26),inset_0_1px_0_rgba(255,255,255,0.25)]',
                  isActive && 'ring-2 ring-[#C97A2B] ring-offset-2 ring-offset-transparent',
                )}
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  transformOrigin: 'left center',
                  transform: animate && !revealed ? 'scaleX(0)' : 'scaleX(1)',
                  transition: animate ? 'transform 1.1s cubic-bezier(0.2, 0, 0, 1)' : undefined,
                  transitionDelay: animate ? `${760 + i * 150}ms` : undefined,
                  boxShadow: isActive
                    ? '0 0 20px rgba(26,108,240,0.26), inset 0 1px 0 rgba(255,255,255,0.25), 0 0 0 1px #C97A2B, 0 0 16px rgba(201,122,43,0.55)'
                    : undefined,
                }}
              >
                <span
                  className="absolute -top-[19px] left-0.5 font-mono text-[10.5px] text-primary"
                  style={{
                    opacity: animate && !revealed ? 0 : 0.9,
                    transition: animate ? 'opacity 0.6s ease' : undefined,
                    transitionDelay: animate ? '1.7s' : undefined,
                  }}
                >
                  {formatHM(seg.startMinutes)}
                </span>
              </div>
            );
          }
          if (seg.kind === 'personal') {
            return (
              <div
                key={i}
                title={tooltip}
                className="absolute top-[25px] h-[13px] rounded-[5px] border border-dashed border-[#B39DFF] bg-[#B39DFF]/15 hover:bg-[#B39DFF]/25"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  opacity: animate && !revealed ? 0 : 1,
                  transition: animate ? 'opacity 0.5s ease' : undefined,
                }}
              />
            );
          }
          return (
            <div
              key={i}
              title={tooltip}
              className="absolute top-[27px] h-[10px] rounded-[5px] bg-foreground/55 hover:bg-foreground/70"
              style={{
                left: `${left}%`,
                width: `${width}%`,
                opacity: animate && !revealed ? 0 : 1,
                transition: animate ? 'opacity 0.5s ease' : undefined,
              }}
            />
          );
        })}
        {nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60 && (
          <div
            className="pointer-events-none absolute bottom-[14px] top-[5px] w-px bg-gradient-to-b from-transparent via-foreground/60 to-transparent"
            style={{
              left: `${x(nowMinutes)}%`,
              opacity: animate && !revealed ? 0 : 1,
              transition: animate ? 'opacity 0.6s ease' : undefined,
              transitionDelay: animate ? '1.9s' : undefined,
            }}
            aria-label="Now"
          >
            <span className="now-dot-pulse absolute left-1/2 top-[26px] block h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground shadow-[0_0_0_3px_hsl(var(--card))]" />
          </div>
        )}
      </div>
      <div className="relative mt-2 h-[14px]">
        {hourMarkers.map((h) => (
          <span
            key={h}
            className="absolute font-mono text-[10.5px] text-muted-foreground"
            style={{ left: `${x(h * 60)}%`, transform: 'translateX(-50%)' }}
          >
            {String(h).padStart(2, '0')}:00
          </span>
        ))}
      </div>
    </div>
  );
}

export default DayRibbon;
